/**
 * Starts the tracker and records a screen view on every route change.
 *
 * A sibling of `NativeShell` rather than part of it: that component is about native
 * chrome and already carries three unrelated boot concerns. This one sits in the
 * same place in the tree — inside the Router, above every route — sees exactly the
 * same navigation stream, and can be tested on its own.
 *
 * It renders nothing.
 */

import { useEffect } from "react";
import { useLocation } from "react-router-dom";

import {
  discardAnalytics,
  flushOnNavigation,
  initializeAnalytics,
  setCurrentScreen,
  track,
} from "~/lib/analytics";
import { initializeAppState } from "~/lib/app-state";
import { screenPatternFor } from "~/lib/screens";
import { useAuthStore } from "~/store/auth-store";
import { AnalyticsEventName } from "~/types/analytics";

export function AnalyticsTracker() {
  const location = useLocation();
  const status = useAuthStore((state) => state.status);
  const userId = useAuthStore((state) => state.user?.id ?? null);
  const locale = useAuthStore((state) => state.user?.locale ?? "cs");

  // The foreground listener is app-wide and outlives any one session, so it is
  // started once rather than per sign-in.
  useEffect(() => {
    let dispose: (() => void) | undefined;
    let disposed = false;
    void initializeAppState().then((cleanup) => {
      if (disposed) {
        cleanup();
        return;
      }
      dispose = cleanup;
    });
    return () => {
      disposed = true;
      dispose?.();
    };
  }, []);

  useEffect(() => {
    if (status !== "authenticated" || userId === null) return;
    void initializeAnalytics(userId, locale);
  }, [status, userId, locale]);

  // Signing out must not leave the previous member's queue on the device.
  useEffect(() => {
    if (status !== "unauthenticated") return;
    void discardAnalytics();
  }, [status]);

  useEffect(() => {
    const pattern = screenPatternFor(location.pathname);
    setCurrentScreen(pattern);
    if (pattern === null) return;
    track(AnalyticsEventName.ScreenView, { screen: pattern });
    flushOnNavigation();
  }, [location.pathname]);

  return null;
}
